import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

/* ── Local design tokens (matches SalesManagerPersona.jsx) ───────────────── */
const C = {
  indigo: "#4338ca", indigo2: "#6366f1", indigoSoft: "#eef2ff", indigoBd: "#c7d2fe",
  green: "#16a34a", greenSoft: "#f0fdf4", greenBd: "#bbf7d0",
  amber: "#d97706", amberSoft: "#fffbeb", amberBd: "#fde68a",
  sky: "#0284c7", skySoft: "#f0f9ff", skyBd: "#bae6fd",
  white: "#ffffff", border: "#e2e8f0", border2: "#f1f5f9",
  txt1: "#0f172a", txt2: "#334155", txt3: "#64748b", txt4: "#94a3b8",
  sans: "'Geist', system-ui, sans-serif",
};

/* ── Avatar helpers ─────────────────────────────────────────────────────── */
const AV_COLORS = ["#6366f1", "#0ea5e9", "#8b5cf6", "#ec4899", "#f59e0b", "#22c55e", "#ef4444"];
const avColor = (n) => {
  if (!n) return C.txt4;
  let h = 0;
  for (let i = 0; i < n.length; i++) h = (h * 31 + n.charCodeAt(i)) & 0xffffffff;
  return AV_COLORS[Math.abs(h) % AV_COLORS.length];
};
const initials = (n) => {
  if (!n) return "?";
  const p = n.trim().split(/\s+/);
  return (p.length >= 2 ? p[0][0] + p[1][0] : p[0].slice(0, 2)).toUpperCase();
};

/* ── Styled atoms ────────────────────────────────────────────────────────── */
const FilterPill = styled("button", {
  shouldForwardProp: (p) => p !== "active",
})(({ active }) => ({
  border: `1.5px solid ${active ? C.indigo : C.border}`,
  background: active ? C.indigoSoft : C.white,
  color: active ? C.indigo : C.txt2,
  padding: "7px 14px",
  borderRadius: 6,
  fontFamily: C.sans,
  fontWeight: active ? 600 : 500,
  fontSize: 12.5,
  cursor: "pointer",
  transition: "all 0.15s ease",
  outline: "none",
  whiteSpace: "nowrap",
  "&:hover": {
    borderColor: C.indigo,
    background: active ? C.indigoSoft : "#f5f3ff",
  },
}));

const SearchField = styled(TextField)({
  flex: 1,
  fontFamily: C.sans,
  "& .MuiOutlinedInput-root": {
    background: C.white,
    borderRadius: 8,
    fontSize: 13.5,
    fontFamily: C.sans,
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: C.indigoBd },
    "&.Mui-focused fieldset": { borderColor: C.indigo, borderWidth: 1.5 },
    "& input::placeholder": { color: C.txt4, opacity: 1 },
  },
});

const statusChipSx = (configured) => ({
  background: configured ? C.greenSoft : C.amberSoft,
  color: configured ? C.green : C.amber,
  fontFamily: C.sans,
  fontWeight: 600,
  fontSize: 11.5,
  height: 22,
  borderRadius: "6px",
  border: `1px solid ${configured ? C.greenBd : C.amberBd}`,
  "& .MuiChip-label": { px: 0.85 },
});

const headCellSx = {
  fontFamily: C.sans,
  fontWeight: 700,
  fontSize: 11.5,
  letterSpacing: 0.4,
  textTransform: "uppercase",
  color: C.txt3,
  background: C.border2,
  borderBottom: `1px solid ${C.border}`,
  py: 1.25,
};

const bodyCellSx = {
  fontFamily: C.sans,
  fontSize: 13.5,
  color: C.txt1,
  borderBottom: `1px solid ${C.border2}`,
  py: 1.5,
};

/* ── Empty state ─────────────────────────────────────────────────────────── */
function EmptyState({ icon, title, subtitle }) {
  return (
    <Box
      sx={{
        background: C.white,
        border: `1px solid ${C.border}`,
        borderRadius: 10,
        p: { xs: 3, md: 5 },
        textAlign: "center",
        animation: "fadeUp 0.35s ease both",
      }}
    >
      {icon && (
        <Box
          sx={{
            width: 56, height: 56, borderRadius: "50%",
            background: C.indigoSoft, color: C.indigo,
            display: "flex", alignItems: "center", justifyContent: "center",
            mx: "auto", mb: 2,
          }}
        >
          {icon}
        </Box>
      )}
      <Typography sx={{ fontFamily: C.sans, fontWeight: 700, fontSize: 17, color: C.txt1 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ fontFamily: C.sans, fontSize: 13.5, color: C.txt3, mt: 0.5 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

/* ── Main component ──────────────────────────────────────────────────────── */
function PersonaTeamList({ members, isLeafUser, onSelect }) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // 'all' | 'configured' | 'pending'

  const configuredCount = useMemo(
    () => members.filter((m) => m.configured).length,
    [members]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return members.filter((m) => {
      if (filter === "configured" && !m.configured) return false;
      if (filter === "pending" && m.configured) return false;
      if (q && !(m.name || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [members, search, filter]);

  /* Empty: leaf user */
  if (isLeafUser) {
    return (
      <EmptyState
        icon={<GroupsOutlinedIcon sx={{ fontSize: 28 }} />}
        title="No team members below you"
        subtitle="Personas are configured by managers for their direct or indirect reports. Your manager will set yours."
      />
    );
  }

  /* Empty: mid-level with no active descendants */
  if (members.length === 0) {
    return <EmptyState title="No active sales managers in your team" />;
  }

  return (
    <>
      {/* Summary banner */}
      <Box
        sx={{
          mb: 2,
          background: "linear-gradient(135deg, #f0f9ff 0%, #eef2ff 100%)",
          border: `1px solid ${C.skyBd}`,
          borderRadius: 10,
          p: { xs: 1.75, md: 2 },
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            width: 32, height: 32, borderRadius: 7,
            background: C.sky, color: C.white,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <GroupsOutlinedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box>
          <Typography sx={{ fontFamily: C.sans, fontWeight: 700, fontSize: 14, color: C.txt1 }}>
            {configuredCount} of {members.length} configured
          </Typography>
          <Typography sx={{ fontFamily: C.sans, fontSize: 12.5, color: C.txt3, mt: 0.25 }}>
            Set up each manager's persona to start auto-matching unclaimed leads to them.
          </Typography>
        </Box>
      </Box>

      {/* Search + filter row */}
      <Box
        sx={{
          mb: 1.5,
          display: "flex",
          gap: 1.25,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
        }}
      >
        <SearchField
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search managers by name…"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: C.txt4, fontSize: 18 }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")} sx={{ color: C.txt4 }}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
          <FilterPill type="button" active={filter === "all"} onClick={() => setFilter("all")}>
            All ({members.length})
          </FilterPill>
          <FilterPill
            type="button"
            active={filter === "configured"}
            onClick={() => setFilter("configured")}
          >
            Configured ({configuredCount})
          </FilterPill>
          <FilterPill
            type="button"
            active={filter === "pending"}
            onClick={() => setFilter("pending")}
          >
            Pending ({members.length - configuredCount})
          </FilterPill>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer
        sx={{
          background: C.white,
          border: `1px solid ${C.border}`,
          borderRadius: 10,
          overflow: "auto",
          animation: "fadeUp 0.3s ease both",
        }}
      >
        <Table sx={{ minWidth: 480 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Manager</TableCell>
              <TableCell sx={headCellSx}>Status</TableCell>
              <TableCell sx={{ ...headCellSx, width: 56 }} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={3}
                  sx={{
                    ...bodyCellSx,
                    textAlign: "center",
                    color: C.txt3,
                    py: 4,
                    fontStyle: "italic",
                  }}
                >
                  No managers match "{search}".
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((m) => (
                <TableRow
                  key={m.userId}
                  onClick={() => onSelect(m)}
                  sx={{
                    cursor: "pointer",
                    transition: "background 0.12s ease",
                    "&:hover": { background: C.indigoSoft },
                    "&:last-child td": { borderBottom: 0 },
                  }}
                >
                  <TableCell sx={bodyCellSx}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 36, height: 36, borderRadius: 8,
                          background: avColor(m.name), color: C.white,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontFamily: C.sans, fontWeight: 700, fontSize: 12.5,
                          flexShrink: 0,
                        }}
                      >
                        {initials(m.name)}
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: C.sans, fontWeight: 600, fontSize: 13.5,
                          color: C.txt1,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}
                      >
                        {m.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    <Chip
                      label={m.configured ? "Configured" : "Not configured"}
                      sx={statusChipSx(m.configured)}
                    />
                  </TableCell>
                  <TableCell sx={bodyCellSx} align="right">
                    <ChevronRightIcon sx={{ color: C.txt4, fontSize: 22 }} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

export default PersonaTeamList;
